import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogHeader,
    GridRow,
    Icon,
    Input,
    MultilineInput,
    Select,
    SelectOption,
    ToastLevel,
    useIntl,
    useToast,
} from "@metsooutotec/modes-react-components"
import { FC, useEffect, useState } from "react"
import { WSUser } from "features/users/models/users-models"
import { validateEmail, validateText, validateSelect } from "validators/inputValidators"
import styles from "./user-modal.module.css"
import customerStore from "features/organizations/customers/store/customer-store"
import { convertValueToOption } from "components/common/select-component-helper"
import usersStore from "features/users/store/users-store"
import { useNavigate } from "react-router-dom"
import { NavigationModalOptions } from "components/utils/modal-helper"
import { observer } from "mobx-react-lite"

interface WSUserModalProps {
    isOpen: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    user?: WSUser
}

const WSUserModal: FC<WSUserModalProps> = observer(({ isOpen, setIsOpen, user }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const navigate = useNavigate()
    const { customers } = customerStore
    const { userLoading, createEntity, editEntity } = usersStore

    const customerOptions: SelectOption[] = customers.map(c => ({
        text: c.name,
        value: c.id,
    }))

    const initializeForm = (u?: WSUser): WSUser => ({
        id: "",
        displayName: "",
        email: "",
        jobTitle: "",
        description: "",
        customerId: "",
        lastAuthEvent: undefined,
        permissions: [],
        roles: [],
        ...(u || {}),
    })

    const [formData, setFormData] = useState<WSUser>(initializeForm)
    const [formErrors, setFormErrors] = useState<Record<string, string>>({})
    const [isModified, setIsModified] = useState<boolean>(false)
    const isEdit = !!user

    useEffect(() => {
        if (isOpen) {
            setFormData(initializeForm(user))
            setFormErrors({})
            setIsModified(false)
        }
    }, [user, isOpen])

    const handleChange = (field: keyof WSUser) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }))
        setIsModified(true)
    }

    const handleValidation = (field: keyof WSUser, error?: string) => {
        setFormErrors(prev => ({ ...prev, [field]: error ?? "" }))
    }

    const onClearClick = (field: keyof WSUser) => {
        setFormData(prev => ({ ...prev, [field]: "" }))
        setFormErrors(prev => ({ ...prev, [field]: "" }))
    }

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {}
        let hasError = false

        if (!formData.customerId) {
            errors.customerId = formatMessage("validation.required")
            hasError = true
        }

        inputConfig.forEach(({ id, validate, isRequired }) => {
            if (isRequired) {
                const value = formData[id] as string
                const error = validate(value)
                if (error) {
                    errors[id] = error
                    hasError = true
                }
            }
        })

        setFormErrors(errors)
        return !hasError
    }

    const handleSave = async () => {
        if (!validateForm()) return
        try {
            const result = isEdit ? await editEntity(formData) : await createEntity(formData)

            if (!result.passed) {
                setMessage(isEdit ? "User update failed!" : "User creation failed!", ToastLevel.ALARM)
                return
            }

            setMessage("Completed!", ToastLevel.SUCCESS)

            if (isEdit) {
                setIsOpen(false)
            } else {
                navigate(`/users-management/${result.entityId}`, {
                    state: { type: "created" } satisfies NavigationModalOptions,
                })
            }
        } catch (error) {
            console.error(error)
            setMessage("Error! Please check logs.", ToastLevel.ALARM)
        }
    }

    const inputConfig: {
        id: keyof WSUser
        label: string
        placeholder: string
        type: string
        validate: (value: string) => string
        isRequired: boolean
    }[] = [
        {
            id: "displayName",
            label: formatMessage("user.displayName"),
            placeholder: formatMessage("user.displayName"),
            type: "text",
            validate: validateText,
            isRequired: true,
        },
        {
            id: "email",
            label: formatMessage("user.email"),
            placeholder: formatMessage("user.email"),
            type: "email",
            validate: validateEmail,
            isRequired: true,
        },
        {
            id: "jobTitle",
            label: formatMessage("user.jobTitle"),
            placeholder: formatMessage("user.jobTitle"),
            type: "text",
            validate: validateText,
            isRequired: false,
        },
    ]

    const onCustomerChange = (picked: SelectOption) => {
        setFormData(prev => ({
            ...prev,
            customerId: picked.value as string,
        }))
        setIsModified(true)
    }

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} size='tiny'>
            <DialogHeader>{isEdit ? formatMessage("userModal.editUser") : formatMessage("userModal.addNewUser")}</DialogHeader>
            <DialogContent>
                {inputConfig.map(({ id, label, placeholder, type, validate, isRequired }) => (
                    <GridRow key={id} className={styles.inputGridRow}>
                        <Input
                            id={id}
                            label={label}
                            placeholder={placeholder}
                            type={type}
                            required={isRequired}
                            uiRequired={isRequired}
                            spanning
                            validate={validate}
                            value={(formData[id] as string) ?? ""}
                            error={formErrors[id]}
                            onValidation={err => handleValidation(id, err)}
                            onChange={handleChange(id)}
                            icon={formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} />}
                        />
                    </GridRow>
                ))}

                <GridRow className={styles.inputGridRow}>
                    <Select
                        label={formatMessage("user.organization")}
                        value={customerOptions.find(opt => opt.value === formData.customerId)?.value ?? ""}
                        options={customerOptions}
                        error={formErrors.customerId}
                        onChange={value => onCustomerChange(convertValueToOption(value, customerOptions))}
                        required
                        uiRequired
                        icon={<Icon className='icon-search-16' />}
                        validate={validateSelect}
                        onValidation={err => handleValidation("customerId", err)}
                        spanning
                    />
                </GridRow>

                <GridRow className={styles.inputGridRow}>
                    <MultilineInput
                        label={formatMessage("user.description")}
                        value={formData.description}
                        onChange={handleChange("description")}
                        placeholder={formatMessage("user.description")}
                    />
                </GridRow>
            </DialogContent>
            <DialogActions>
                <Button children={formatMessage("common.cancel")} onClick={() => setIsOpen(false)} flexible centered />
                <Button
                    children={formatMessage("common.save")}
                    onClick={handleSave}
                    primary
                    disabled={!isModified || userLoading}
                    loading={userLoading}
                    flexible
                    centered
                />
            </DialogActions>
        </Dialog>
    )
})

export default WSUserModal
